import { DateTime } from "luxon";
import { ClockIcon, VideoIcon } from "lucide-react";

import { DatePicker } from "./DatePicker";
import { TimezoneSelect } from "./TimezoneSelect";

import { Modal } from "../ui/Modal";

import { cn, generateTimeSlots } from "../../lib/utils";

import { useSelectMeetingDateModal } from "../../hooks/useSelectMeetingDateModal";
import { useMeetingDetailsModal } from "../../hooks/useMeetingDetailsModal";
import { useMeetingDetails } from "../../hooks/useMeetingDetails";
import { useGetBookings } from "../../hooks/useGetBookings";

import RyanAvatar from "../../images/ryan-avatar.png";

export const SelectMeetingDateModal = () => {
  const { isOpen, onClose: onSMDMClose } = useSelectMeetingDateModal();
  const { onOpen: onMDMOpen } = useMeetingDetailsModal();
  const { time, date, userTimezone, hostTimezone, setTime } =
    useMeetingDetails();
  const { data: bookings } = useGetBookings();

  const timeSlots = generateTimeSlots("09:00", "17:00", 30);
  const bookedSlots = new Set((bookings || []).map((b: any) => b.startTime));

  const getSlot = (time: string) => {
    if (!date) return null;

    const [hour, minute] = time.split(":");

    return DateTime.fromJSDate(date, {
      zone: hostTimezone,
    })
      .set({
        hour: Number(hour),
        minute: Number(minute),
        second: 0,
        millisecond: 0,
      })
      .toUTC()
      .toISO();
  };

  const convertSlot = (time: string) => {
    if (!date) return time;

    const [hour, minute] = time.split(":");
    const base = DateTime.fromJSDate(date, {
      zone: hostTimezone,
    }).set({
      hour: Number(hour),
      minute: Number(minute),
    });

    return base.setZone((userTimezone as any).value).toFormat("HH:mm");
  };

  const isSlotBooked = (time: string) => {
    const slot = getSlot(time);
    if (!slot) return false;

    return bookedSlots.has(slot);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onSMDMClose}
      className="flex flex-col lg:flex-row"
    >
      <div className="w-full lg:max-w-96 p-7 space-y-6 border-b lg:border-b-0 lg:border-r border-gray-800 flex flex-col justify-center">
        <div className="space-y-2">
          <img src={RyanAvatar} alt="Ryan" className="size-[100px]" />
          <h1 className="text-[28px] font-bold text-white">Ryan Rayner</h1>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-x-2">
            <ClockIcon className="text-white shrink-0" />
            <p className="font-bold text-gray-500">30 min</p>
          </div>

          <div className="flex items-center gap-x-2">
            <VideoIcon className="text-white shrink-0" />
            <p className="font-bold text-gray-500">
              Web conferencing details provided upon confirmation.
            </p>
          </div>
        </div>
      </div>

      <div className="p-7 space-y-[22px]">
        <h2 className="text-xl font-bold text-white">Select a Date & Time</h2>
        <DatePicker />

        <div className="space-y-1.5">
          <h2 className="text-white font-bold">Time zone</h2>
          <TimezoneSelect />
        </div>
      </div>

      {date && (
        <div className="p-7 space-y-[22px] border-t lg:border-t-0 lg:border-l border-gray-800">
          <h2 className="text-white">
            {date.toLocaleDateString("en-US", {
              weekday: "long",
              month: "long",
              day: "numeric",
            })}
          </h2>

          <div className="max-h-[380px] overflow-y-auto scrollbar-hidden space-y-4 flex flex-col">
            {timeSlots.map((timeSlot) => {
              const convertedTime = convertSlot(timeSlot);
              const isSelected = convertedTime === time;
              const isBooked = isSlotBooked(timeSlot);

              return (
                <button
                  key={timeSlot}
                  disabled={isBooked}
                  onClick={() => {
                    if (isBooked) return;
                    setTime(convertedTime);
                    onSMDMClose();
                    onMDMOpen();
                  }}
                  className={cn(
                    "w-full lg:w-[236px] py-3 px-6 border rounded-lg font-bold transition-colors duration-300",
                    isBooked
                      ? "bg-gray-700 border-gray-700 text-gray-400 cursor-not-allowed"
                      : isSelected
                        ? "bg-[#01C2F3] border-[#01C2F3] text-[#09172E]"
                        : "border-[#057DA4] text-[#01C2F3] hover:bg-[#08253D] hover:border-[#08253D]",
                  )}
                >
                  {convertedTime}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </Modal>
  );
};
